/** Top-bar avatar button opening the account menu. */
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/** One entry of the account menu: either a route link or an action. */
export type UserMenuItem =
  | { key: string; label: string; to: string }
  | { key: string; label: string; onSelect: () => void; tone?: 'danger' };

export interface UserMenuProps {
  /** Initials shown in the avatar circle, e.g. `"BT"`. */
  initials: string;
  /** Full name shown at the top of the open menu. */
  name: string;
  /** Address shown under the name, to tell two accounts apart. */
  email: string;
  /** Entries to render, in order. */
  items: UserMenuItem[];
  /** Accessible label of the avatar button. */
  buttonLabel: string;
}

/**
 * Render the avatar button and, once it is activated, the account menu
 * anchored under it.
 *
 * The menu closes on selection, on `Escape` (returning focus to the button)
 * and on a click outside it, so it never traps a volunteer mid-tour.
 *
 * @param props - Identity to display and the entries to offer.
 * @returns The avatar button, with the menu when it is open.
 */
export function UserMenu({ initials, name, email, items, buttonLabel }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;

    /**
     * Close the menu when a pointer goes down outside of it.
     *
     * @param event - The originating pointer event.
     */
    function handlePointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    /**
     * Close the menu on `Escape` and hand focus back to the avatar button.
     *
     * @param event - The originating keyboard event.
     */
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className="ui-usermenu" ref={containerRef}>
      <button
        type="button"
        className="ui-topbar__avatar ui-usermenu__button"
        aria-label={buttonLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((current) => !current)}
        ref={buttonRef}
      >
        {initials}
      </button>
      {open ? (
        <div className="ui-usermenu__panel" id={menuId} role="menu">
          <div className="ui-usermenu__identity">
            <strong>{name}</strong>
            <span>{email}</span>
          </div>
          {items.map((item) =>
            'to' in item ? (
              <Link
                key={item.key}
                className="ui-usermenu__item"
                role="menuitem"
                to={item.to}
                onClick={() => setOpen(false)}
              >
                {item.label}
              </Link>
            ) : (
              <button
                key={item.key}
                type="button"
                className={[
                  'ui-usermenu__item',
                  item.tone === 'danger' ? 'ui-usermenu__item--danger' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}
